import { MasterDocument } from '@util/firestore/master';
import { trackMasterEvent } from '@util/analytics/masterTracking';

interface YouTubeVideo {
  videoId: string;
  url: string;
  title: string;
  channel: string;
  date?: string;
  views?: number;
  duration?: number;
  featured?: boolean;
}

interface ReviewsSectionProps {
  master: MasterDocument;
  additionalVideos?: YouTubeVideo[];
}

export default function ReviewsSection({ master, additionalVideos }: ReviewsSectionProps) {
  const hasReviews = master.reviews && master.reviews.length > 0;
  const hasVideos = additionalVideos && additionalVideos.length > 0;

  if (!hasReviews && !hasVideos) {
    return null;
  }

  return (
    <div>
      <ul role="list" aria-label="Reviews and Videos" className="space-y-[1.2rem]">
        {/* Written reviews */}
        {master.reviews?.map((review, index) => (
          <li key={`review-${index}`} role="listitem">
            <a
              href={review.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-[1rem] group"
              onClick={() =>
                trackMasterEvent('master_review_click', master, {
                  section: 'reviews',
                  review_source: review.source,
                  review_url: review.url,
                })
              }
            >
              <div>
                <span className="text-[1.5rem] text-brand-secondary group-hover:underline font-medium">
                  {review.title}
                </span>
                <br />
                <span className="text-[1.3rem] text-gray-500">
                  {review.source}
                  {review.date ? ` · ${review.date}` : ''}
                </span>
              </div>
            </a>
          </li>
        ))}

        {/* Additional YouTube videos as list items */}
        {additionalVideos?.map((video) => (
          <li key={`video-${video.videoId}`} role="listitem">
            <a
              href={video.url || `https://www.youtube.com/watch?v=${video.videoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-[1rem] group"
            >
              {/* Small YouTube icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-[1.6rem] h-[1.6rem] mt-[0.2rem] flex-shrink-0 text-red-600"
                aria-hidden="true"
              >
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              <div>
                <span className="text-[1.5rem] text-brand-secondary group-hover:underline font-medium">
                  {video.title}
                </span>
                <br />
                <span className="text-[1.3rem] text-gray-500">
                  {video.channel}
                </span>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
